import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { PaginatedResponse } from '../types'

export interface UsuarioResumo {
  id: string
  nome_completo: string
  email: string
  perfil: string
  perfil_display?: string
  registro_profissional?: string
  ativo?: boolean
}

interface UsuarioListParams {
  perfil?: string
  page_size?: number
}

export interface UsuarioCreateInput {
  nome_completo: string
  email: string
  perfil: string
  senha: string
  registro_profissional?: string
}

export function useUsuarioList(params?: UsuarioListParams) {
  return useQuery({
    queryKey: ['usuarios', params],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<UsuarioResumo>>('/auth/usuarios/', { params })
      return r.data
    },
    staleTime: 1000 * 60 * 5,
  })
}

export function useUsuarioDetalhe(id: string) {
  return useQuery({
    queryKey: ['usuarios', id],
    queryFn: async () => {
      const r = await api.get<UsuarioResumo>(`/auth/usuarios/${id}/`)
      return r.data
    },
    enabled: !!id,
  })
}

export function useUsuarioCreate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: UsuarioCreateInput) => {
      const r = await api.post<UsuarioResumo>('/auth/usuarios/', data)
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['usuarios'] }),
  })
}

export function useTrocarSenha() {
  return useMutation({
    mutationFn: async (data: { senha_atual: string; senha_nova: string }) => {
      const r = await api.post('/auth/trocar-senha/', data)
      return r.data
    },
  })
}

export interface TotpSetupResponse {
  secret: string
  otpauth_uri: string
  qr_code_base64: string
}

export function useTotpSetup() {
  return useMutation({
    mutationFn: async () => {
      const r = await api.post<TotpSetupResponse>('/auth/2fa/setup/')
      return r.data
    },
  })
}

export function useTotpConfirmar() {
  return useMutation({
    mutationFn: async (totp: string) => {
      const r = await api.post('/auth/2fa/confirmar/', { totp })
      return r.data
    },
  })
}

export function useTotpDesativar() {
  return useMutation({
    mutationFn: async (senha_atual: string) => {
      const r = await api.post('/auth/2fa/desativar/', { senha_atual })
      return r.data
    },
  })
}

export function useUsuarioUpdate(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<UsuarioResumo>) => {
      const r = await api.patch<UsuarioResumo>(`/auth/usuarios/${id}/`, data)
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['usuarios'] })
      qc.invalidateQueries({ queryKey: ['usuarios', id] })
    },
  })
}
